/**
 * Resume before/after comparison — pure, database-free.
 *
 * Two near-identical copies of this diff used to exist: one inside
 * `POST /api/resume-builder/refine/changes` (which could only diff two rows it
 * loaded from Postgres) and one inside `ResumeAnalyzeDialog` (which diffed two
 * in-memory drafts). They had already drifted — different clip lengths,
 * different caps, and only the client copy noticed custom-section edits.
 *
 * The route was the wrong home for the logic. A resume comparison is a pure
 * function of two documents; requiring both to be persisted rows first is an
 * accident of where the first caller happened to sit. The improve flows rewrite
 * content that has never been written to the database (`persist: false` drafts,
 * a single field the user has not saved yet), so an id-only diff cannot serve
 * them at all.
 *
 * So the diff lives here, takes plain `ResumeContent` objects, and can run on
 * either side of the wire. The route stays as a thin, authenticated wrapper for
 * the id-based callers it already had, and every other flow calls this module
 * directly with the documents it is already holding.
 */

import { normalizeTextValue } from './normalize-content';
import { DEFAULT_RESUME_CONTENT, type ResumeContent } from './types';

export type ResumeCompareSection =
  | 'profile'
  | 'skills'
  | 'employment'
  | 'education'
  | 'certifications'
  | 'customSections';

export const RESUME_COMPARE_SECTIONS: readonly ResumeCompareSection[] = [
  'profile',
  'skills',
  'employment',
  'education',
  'certifications',
  'customSections',
];

export const RESUME_COMPARE_SECTION_LABELS: Record<ResumeCompareSection, string> = {
  profile: 'Profile',
  skills: 'Skills',
  employment: 'Experience',
  education: 'Education',
  certifications: 'Certifications',
  customSections: 'Custom sections',
};

/**
 * One human-readable difference between two resume documents.
 *
 * `before`/`after` are absent rather than empty when that side has no text, so
 * a renderer can say "added" / "removed" instead of printing a blank line.
 */
export interface ResumeChangeItem {
  id: string;
  /** A `ResumeCompareSection`, or a caller-chosen key for field-level diffs. */
  section: string;
  label: string;
  before?: string;
  after?: string;
}

export interface ResumeCompareOptions {
  /** Hard cap on returned items, so one wholesale rewrite cannot flood the UI. */
  maxChanges?: number;
  /** Per-value clip length. Comparison is a summary, not a full document view. */
  maxValueLength?: number;
  /**
   * Sections to diff. Defaults to all of them; callers pin this only when they
   * must keep an already-shipped payload shape stable.
   */
  sections?: readonly ResumeCompareSection[];
}

const DEFAULT_MAX_CHANGES = 30;
const DEFAULT_MAX_VALUE_LENGTH = 260;

/**
 * Collapse a value to comparable plain text.
 *
 * Goes through `normalizeTextValue` because rich-text fields
 * (`employment.responsibilities`, `customSections.content`) are not always
 * strings on disk — older resumes hold editor documents there, and comparing
 * `[object Object]` against `[object Object]` reports "no change" forever.
 */
export function normalizeComparableText(value: unknown): string {
  return normalizeTextValue(value).replace(/\s+/g, ' ').trim();
}

export function clipComparableText(value: string, maxLength = DEFAULT_MAX_VALUE_LENGTH): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1).trimEnd()}...`;
}

/**
 * Fill in the keys a diff reads so a partially-shaped document (anything read
 * back out of a JSONB column) cannot throw on `.profile.summary` or `.length`.
 */
export function toComparableResumeContent(content: unknown): ResumeContent {
  if (!content || typeof content !== 'object') {
    return DEFAULT_RESUME_CONTENT;
  }

  const partial = content as Partial<ResumeContent>;

  return {
    ...DEFAULT_RESUME_CONTENT,
    ...partial,
    personalInfo: { ...DEFAULT_RESUME_CONTENT.personalInfo, ...(partial.personalInfo || {}) },
    profile: { ...DEFAULT_RESUME_CONTENT.profile, ...(partial.profile || {}) },
    contact: { ...DEFAULT_RESUME_CONTENT.contact, ...(partial.contact || {}) },
    skills: { ...DEFAULT_RESUME_CONTENT.skills, ...(partial.skills || {}) },
    links: Array.isArray(partial.links) ? partial.links : [],
    employment: Array.isArray(partial.employment) ? partial.employment : [],
    education: Array.isArray(partial.education) ? partial.education : [],
    certifications: Array.isArray(partial.certifications) ? partial.certifications : [],
    customSections: Array.isArray(partial.customSections) ? partial.customSections : [],
  };
}

function pushTextChange(
  changes: ResumeChangeItem[],
  maxValueLength: number,
  section: string,
  label: string,
  before: unknown,
  after: unknown
): void {
  const previous = normalizeComparableText(before);
  const next = normalizeComparableText(after);
  if (previous === next) return;

  changes.push({
    id: `${section}-${changes.length + 1}`,
    section,
    label,
    before: previous ? clipComparableText(previous, maxValueLength) : undefined,
    after: next ? clipComparableText(next, maxValueLength) : undefined,
  });
}

/**
 * Skill lists are unordered sets to a reader, so report membership rather than
 * position — reordering the same skills is not a change worth showing.
 */
function summarizeArrayDelta(
  before: string[],
  after: string[],
  maxValueLength: number
): { before?: string; after?: string } | null {
  const previousMap = new Map<string, string>();
  for (const value of before) {
    const text = normalizeComparableText(value);
    if (text) previousMap.set(text.toLowerCase(), text);
  }

  const nextMap = new Map<string, string>();
  for (const value of after) {
    const text = normalizeComparableText(value);
    if (text) nextMap.set(text.toLowerCase(), text);
  }

  const removed = Array.from(previousMap.entries())
    .filter(([key]) => !nextMap.has(key))
    .map(([, value]) => value);
  const added = Array.from(nextMap.entries())
    .filter(([key]) => !previousMap.has(key))
    .map(([, value]) => value);

  if (removed.length === 0 && added.length === 0) {
    return null;
  }

  return {
    before:
      removed.length > 0
        ? clipComparableText(`Removed: ${removed.join(', ')}`, maxValueLength)
        : undefined,
    after:
      added.length > 0 ? clipComparableText(`Added: ${added.join(', ')}`, maxValueLength) : undefined,
  };
}

/** Summarize the differences between two resume documents, most-visible first. */
export function compareResumeContent(
  before: unknown,
  after: unknown,
  options: ResumeCompareOptions = {}
): ResumeChangeItem[] {
  const maxChanges = options.maxChanges ?? DEFAULT_MAX_CHANGES;
  const maxValueLength = options.maxValueLength ?? DEFAULT_MAX_VALUE_LENGTH;
  const sections = new Set(options.sections ?? RESUME_COMPARE_SECTIONS);

  const previous = toComparableResumeContent(before);
  const next = toComparableResumeContent(after);
  const changes: ResumeChangeItem[] = [];

  if (sections.has('profile')) {
    pushTextChange(
      changes,
      maxValueLength,
      'profile',
      'Professional summary',
      previous.profile.summary,
      next.profile.summary
    );
    pushTextChange(
      changes,
      maxValueLength,
      'profile',
      'Professional headline',
      previous.profile.headline,
      next.profile.headline
    );
  }

  if (sections.has('skills')) {
    const technicalDelta = summarizeArrayDelta(
      previous.skills.technical || [],
      next.skills.technical || [],
      maxValueLength
    );
    if (technicalDelta) {
      changes.push({
        id: `skills-${changes.length + 1}`,
        section: 'skills',
        label: 'Technical skills',
        before: technicalDelta.before,
        after: technicalDelta.after,
      });
    }

    const softDelta = summarizeArrayDelta(
      previous.skills.soft || [],
      next.skills.soft || [],
      maxValueLength
    );
    if (softDelta) {
      changes.push({
        id: `skills-${changes.length + 1}`,
        section: 'skills',
        label: 'Soft skills',
        before: softDelta.before,
        after: softDelta.after,
      });
    }
  }

  if (sections.has('employment')) {
    const previousEmployment = new Map(previous.employment.map((item) => [item.id, item]));
    const seenEmployment = new Set<string>();

    for (const entry of next.employment) {
      const previousEntry = previousEmployment.get(entry.id);
      const roleLabel = `${entry.title || previousEntry?.title || 'Role'} at ${entry.company || previousEntry?.company || 'Company'}`;

      if (!previousEntry) {
        changes.push({
          id: `employment-${changes.length + 1}`,
          section: 'employment',
          label: `Added role: ${roleLabel}`,
        });
        continue;
      }

      seenEmployment.add(entry.id);
      pushTextChange(
        changes,
        maxValueLength,
        'employment',
        `Updated responsibilities: ${roleLabel}`,
        previousEntry.responsibilities,
        entry.responsibilities
      );
    }

    for (const entry of previous.employment) {
      if (seenEmployment.has(entry.id)) continue;
      const roleLabel = `${entry.title || 'Role'} at ${entry.company || 'Company'}`;
      changes.push({
        id: `employment-${changes.length + 1}`,
        section: 'employment',
        label: `Removed role: ${roleLabel}`,
      });
    }
  }

  if (sections.has('customSections')) {
    const previousSections = new Map(previous.customSections.map((item) => [item.id, item]));
    const seenSections = new Set<string>();

    for (const entry of next.customSections) {
      const previousEntry = previousSections.get(entry.id);
      if (!previousEntry) {
        changes.push({
          id: `customSections-${changes.length + 1}`,
          section: 'customSections',
          label: `Added custom section: ${entry.title || 'Untitled section'}`,
        });
        continue;
      }

      seenSections.add(entry.id);
      pushTextChange(
        changes,
        maxValueLength,
        'customSections',
        `Updated custom section: ${entry.title || previousEntry.title || 'Untitled section'}`,
        previousEntry.content,
        entry.content
      );
    }

    for (const entry of previous.customSections) {
      if (seenSections.has(entry.id)) continue;
      changes.push({
        id: `customSections-${changes.length + 1}`,
        section: 'customSections',
        label: `Removed custom section: ${entry.title || 'Untitled section'}`,
      });
    }
  }

  // Education and certifications carry no prose worth diffing line by line, so
  // only a change in how many entries there are is reported.
  if (sections.has('education') && previous.education.length !== next.education.length) {
    changes.push({
      id: `education-${changes.length + 1}`,
      section: 'education',
      label: 'Education entries changed',
      before: `Before: ${previous.education.length} entries`,
      after: `After: ${next.education.length} entries`,
    });
  }

  if (
    sections.has('certifications') &&
    previous.certifications.length !== next.certifications.length
  ) {
    changes.push({
      id: `certifications-${changes.length + 1}`,
      section: 'certifications',
      label: 'Certifications entries changed',
      before: `Before: ${previous.certifications.length} entries`,
      after: `After: ${next.certifications.length} entries`,
    });
  }

  return changes.slice(0, maxChanges);
}

/**
 * Diff a single field rewrite.
 *
 * The field-level improve flow never builds a second `ResumeContent` — it
 * rewrites one passage in place — so it would otherwise have to fabricate a
 * whole document just to be diffable.
 */
export function compareResumeField(
  input: { section?: string; label: string; before: unknown; after: unknown },
  options: Pick<ResumeCompareOptions, 'maxValueLength'> = {}
): ResumeChangeItem[] {
  const changes: ResumeChangeItem[] = [];
  pushTextChange(
    changes,
    options.maxValueLength ?? DEFAULT_MAX_VALUE_LENGTH,
    input.section ?? 'field',
    input.label,
    input.before,
    input.after
  );
  return changes;
}
